type RichTextNode = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: RichTextNode[];
  children?: RichTextNode[];
  root?: RichTextNode;
  fileName?: string;
  mimeType?: string;
};

const BLOCK_NODE_TYPES = new Set([
  'paragraph',
  'heading',
  'blockquote',
  'bulletList',
  'orderedList',
  'listItem',
  'codeBlock',
]);

const EMBED_NODE_TYPES = new Set(['image', 'file', 'attachment', 'asset', 'asset-image', 'asset-attachment']);

export function serializeRichTextContent(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return undefined;
    }
  }

  const stringified = String(value).trim();
  return stringified.length > 0 ? stringified : undefined;
}

export function projectRichTextToPlainText(value: unknown): string {
  const serialized = serializeRichTextContent(value);
  if (!serialized) {
    return '';
  }

  try {
    const parsed = JSON.parse(serialized) as RichTextNode;
    return normalizeWhitespace(extractPlainText(parsed));
  } catch {
    return normalizeWhitespace(serialized);
  }
}

function extractPlainText(node: unknown): string {
  if (node == null) {
    return '';
  }

  if (typeof node === 'string') {
    return node;
  }

  if (Array.isArray(node)) {
    return node.map(extractPlainText).join('');
  }

  if (typeof node !== 'object') {
    return String(node);
  }

  const richNode = node as RichTextNode;
  if (richNode.root) {
    return extractPlainText(richNode.root);
  }

  if (typeof richNode.text === 'string') {
    return richNode.text;
  }

  if (richNode.type === 'hardBreak') {
    return '\n';
  }

  if (richNode.type && EMBED_NODE_TYPES.has(richNode.type)) {
    const label =
      pickString(richNode.fileName) ||
      pickString(richNode.attrs?.fileName) ||
      pickString(richNode.attrs?.title) ||
      pickString(richNode.attrs?.alt) ||
      (richNode.type === 'image' ? 'Image' : 'Attachment');
    return `[${label}]`;
  }

  const childNodes = Array.isArray(richNode.content)
    ? richNode.content
    : Array.isArray(richNode.children)
      ? richNode.children
      : [];
  const children = childNodes.map(extractPlainText).join('');
  if (richNode.type && BLOCK_NODE_TYPES.has(richNode.type)) {
    return `${children}\n`;
  }

  return children;
}

function pickString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeWhitespace(value: string): string {
  return value
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
